import { useState, useEffect } from "react";
import { useAuth } from "@/lib/auth";
import { useLocation, Link } from "wouter";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import warehouseBg from "@assets/generated_images/modern_industrial_warehouse_interior_with_warm_lighting.png";
import { ArrowRight, Building2, Lock, User, Mail, Eye, EyeOff, Briefcase } from "lucide-react";
import { motion } from "framer-motion";
import { useToast } from "@/hooks/use-toast";
import { useTranslation } from "react-i18next";

export default function Register() {
  const { t } = useTranslation();
  const { register, user, isLoading } = useAuth();
  const [, setLocation] = useLocation();

  useEffect(() => {
    if (!isLoading && user) {
      setLocation("/dashboard");
    }
  }, [user, isLoading, setLocation]);

  const { toast } = useToast();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [companyName, setCompanyName] = useState("");
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);

  if (isLoading) {
    return (
      <div className="min-h-screen w-full flex items-center justify-center bg-background">
        <div className="flex flex-col items-center gap-4">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
        </div>
      </div>
    );
  }

  const handleRegister = async (e: React.FormEvent) => {
    e.preventDefault();
    if (password.length < 6) {
      toast({ title: t("authPages.validationErrorTitle"), description: t("authPages.passwordMin6"), variant: "destructive" });
      return;
    }
    setLoading(true);
    try {
      await register({ email, password, name, companyName });
    } catch (error: any) {
      setLoading(false);
      toast({
        title: t("authPages.registrationFailedTitle"),
        description: error.message || t("authPages.registrationFailedDesc"),
        variant: "destructive",
      });
    }
  };

  return (
    <div className="min-h-screen w-full grid lg:grid-cols-5 overflow-hidden bg-background">
      <div className="col-span-5 lg:col-span-2 relative flex flex-col justify-center p-6 sm:p-8 md:p-12 lg:p-16 z-10">
        <div className="absolute inset-0 bg-[radial-gradient(#e5e7eb_1px,transparent_1px)] [background-size:16px_16px] opacity-30 -z-10" />

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, ease: "easeOut" }}
          className="w-full max-w-sm mx-auto space-y-8"
        >
          <div className="space-y-6">
            <div className="h-16 w-16 bg-primary rounded-2xl flex items-center justify-center shadow-xl shadow-primary/20 rotate-3">
              <Building2 className="h-8 w-8 text-primary-foreground" />
            </div>
            <div className="space-y-2">
              <h1 className="text-3xl font-heading font-bold tracking-tight text-foreground" data-testid="text-register-title">
                {t("authPages.createAccountTitle")}
              </h1>
              <p className="text-muted-foreground text-base font-light">
                {t("authPages.createAccountSubtitle")}
              </p>
            </div>
          </div>

          <form onSubmit={handleRegister} className="space-y-5">
            <div className="space-y-3">
              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ps-1">{t("authPages.fullNameLabel")}</Label>
                <div className="relative group">
                  <User className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input
                    type="text"
                    placeholder={t("authPages.fullNamePlaceholder")}
                    value={name}
                    onChange={(e) => setName(e.target.value)}
                    className="h-12 ps-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    data-testid="input-register-name"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ps-1">{t("authPages.emailLabel")}</Label>
                <div className="relative group">
                  <Mail className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input
                    type="email"
                    placeholder={t("authPages.registerEmailPlaceholder")}
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    className="h-12 ps-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    data-testid="input-register-email"
                  />
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ps-1">{t("authPages.passwordLabel")}</Label>
                <div className="relative group">
                  <Lock className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input
                    type={showPassword ? "text" : "password"}
                    placeholder={t("authPages.passwordMinPlaceholder")}
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    className="h-12 ps-10 pe-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    data-testid="input-register-password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute end-3 top-3 h-5 w-5 text-muted-foreground hover:text-primary transition-colors"
                    data-testid="button-toggle-register-password"
                  >
                    {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                  </button>
                </div>
              </div>

              <div className="space-y-1.5">
                <Label className="text-xs font-bold text-muted-foreground uppercase tracking-wider ps-1">{t("authPages.companyNameLabel")}</Label>
                <div className="relative group">
                  <Briefcase className="absolute start-3 top-3 h-5 w-5 text-muted-foreground group-hover:text-primary transition-colors" />
                  <Input
                    type="text"
                    placeholder={t("authPages.companyNamePlaceholder")}
                    value={companyName}
                    onChange={(e) => setCompanyName(e.target.value)}
                    className="h-12 ps-10 bg-background border-muted-foreground/20 hover:border-primary/50 focus:border-primary focus:ring-4 focus:ring-primary/10 transition-all rounded-lg"
                    required
                    data-testid="input-register-company"
                  />
                </div>
              </div>
            </div>

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-12 text-base font-medium bg-gradient-to-r from-primary to-blue-600 hover:from-blue-600 hover:to-primary text-white shadow-lg shadow-blue-500/25 hover:shadow-blue-500/40 transition-all duration-300 rounded-lg"
              data-testid="button-register-submit"
            >
              {loading ? (
                <span className="flex items-center gap-2">
                  <span className="h-4 w-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  {t("authPages.creatingWorkspace")}
                </span>
              ) : (
                <span className="flex items-center justify-center gap-2">
                  {t("authPages.getStarted")} <ArrowRight className="h-4 w-4" />
                </span>
              )}
            </Button>
          </form>

          <div className="text-center text-sm text-muted-foreground">
            {t("authPages.alreadyHaveAccount")}{" "}
            <Link href="/login" className="text-primary font-medium hover:underline" data-testid="link-login">
              {t("authPages.signInLink")}
            </Link>
          </div>
        </motion.div>
      </div>

      <div className="hidden lg:block col-span-3 relative overflow-hidden bg-black">
        <motion.div
          initial={{ scale: 1.1, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 1.5, ease: "easeOut" }}
          className="absolute inset-0"
        >
          <img
            src={warehouseBg}
            alt={t("authPages.warehouseAlt")}
            className="w-full h-full object-cover opacity-60"
          />
        </motion.div>

        <div className="absolute inset-0 bg-gradient-to-l from-transparent via-black/20 to-black/80" />
        <div className="absolute inset-0 bg-gradient-to-t from-black/90 via-transparent to-transparent" />

        <div className="absolute bottom-0 start-0 w-full p-16 z-20">
          <motion.div
            initial={{ opacity: 0, y: 40 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.4, duration: 0.8 }}
            className="max-w-2xl space-y-8"
          >
            <h2 className="text-5xl font-heading font-bold text-white leading-none tracking-tight">
              {t("authPages.workspaceReadyTitle")} <br />
              <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-blue-200">
                {t("authPages.readyInSeconds")}
              </span>
            </h2>

            <div className="flex flex-col gap-4 border-s-2 border-blue-500/50 ps-6">
              <p className="text-lg text-gray-300 font-light leading-relaxed max-w-lg">
                {t("authPages.registerHeroDesc")}
              </p>
            </div>
          </motion.div>
        </div>
      </div>
    </div>
  );
}
